/// <amd-module name="Wit/QueriesHub/Common/Components/QueryFavoriteButton" />
import { ArtifactScope, Favorite } from "Favorites/Common/Contracts/Favorite";
import { IFavoritesService } from "Favorites/Common/FavoritesService";
import * as React from "react";
import { getTFSPageData, IProjectData } from "Tfs/Platform/Page";
import { css, IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { Button } from "VSSUI/Button";
import { Observer } from "VSSUI/Observer";
import { FavoriteTypes } from "Wit/Common/Generated/Constants";
import { reportError } from "Wit/Common/QueryUtils/ErrorHandlingUtils";
import { QueryItem } from "Wit/Common/QueryUtils/Models";
import * as QueryItemFavoriteConstants from "Wit/Common/QueryUtils/QueriesHubConstants";
import * as Resources from "Wit/QueriesHub/Resources";

export interface IQueryFavoriteButtonProps {
    alwaysVisible?: boolean;
    className?: string;
    item: QueryItem;
    onAddFavorite?: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
    onRemoveFavorite?: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
}
export class QueryFavoriteButton extends VssComponent<IQueryFavoriteButtonProps> {
    private _favoritesService: IFavoritesService;
    private _projectData: IProjectData | undefined;

    constructor(props: IQueryFavoriteButtonProps, context: IVssComponentContext) {
        super(props, context);
        this._projectData = getTFSPageData(this.context.pageContext)?.project;
        this._favoritesService = context.pageContext.getService<IFavoritesService>("IFavoritesService");
    }

    public render() {
        if (!this._favoritesService.canUseFavorites()) {
            return <span />;
        }
        const removeFavorite = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            event.preventDefault();
            event.stopPropagation();
            this._unfavoriteQuery(event);
        };

        const addFavorite = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
            event.preventDefault();
            event.stopPropagation();
            this._favoriteQuery(event);
        };

        return (
            <Observer isFavorite={this.props.item.isFavoriteObservable}>
                {(observableProps: { isFavorite: boolean }) => {
                    let button: JSX.Element;
                    let alwaysVisible = this.props.alwaysVisible;
                    if (observableProps.isFavorite) {
                        alwaysVisible = true;
                        button = (
                            <Button
                                ariaLabel={Resources.Unfavorite}
                                className={css(this.props.className, "favorite-icon-color")}
                                excludeTabStop
                                iconProps={{ iconName: "FavoriteStarFill" }}
                                onClick={removeFavorite}
                                subtle
                            />
                        );
                    } else {
                        button = (
                            <Button
                                ariaLabel={Resources.Favorite}
                                excludeTabStop
                                iconProps={{ iconName: "FavoriteStar" }}
                                onClick={addFavorite}
                                subtle
                            />
                        );
                    }

                    return (
                        <div className={css("flex-column", "flex-center", !alwaysVisible && "bolt-table-cell-content-reveal")}>
                            <div className="flex-row">{button}</div>
                        </div>
                    );
                }}
            </Observer>
        );
    }

    private _favoriteQuery = async (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        if (!this._projectData) {
            return;
        }
        this.props.item.isFavoriteObservable.value = true;
        const scope: ArtifactScope = {
            id: this._projectData.id,
            name: this._projectData.name,
            type: QueryItemFavoriteConstants.FavoriteArtifactScopeType
        };
        const newFavorite = {
            artifactId: this.props.item.id,
            artifactName: this.props.item.name,
            artifactScope: scope,
            artifactType: FavoriteTypes.WIT_QUERYITEM
        } as Favorite;

        try {
            if (this.props.onAddFavorite) {
                this.props.onAddFavorite(event);
            }
            await this._favoritesService.addFavorite(newFavorite);
        } catch (ex) {
            // set the error and favorite value back
            reportError(this.context.pageContext, "favorite-query", ex?.message);
            this.props.item.isFavoriteObservable.value = false;
            if (this.props.onRemoveFavorite) {
                this.props.onRemoveFavorite(event);
            }
        }
    };

    private _unfavoriteQuery = async (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
        this.props.item.isFavoriteObservable.value = false;

        if (this.props.onRemoveFavorite) {
            this.props.onRemoveFavorite(event);
        }

        if (!this._projectData) {
            return;
        }
        const scope: ArtifactScope = {
            id: this._projectData.id,
            name: this._projectData.name,
            type: QueryItemFavoriteConstants.FavoriteArtifactScopeType
        };
        const favorites = await this._favoritesService.getFavorites(FavoriteTypes.WIT_QUERYITEM, scope.type, scope.id, false);

        // this *should* be a single favorite
        const matchedFavorites = favorites.value.filter(favorite => favorite.artifactId === this.props.item.id);
        for (const favorite of matchedFavorites) {
            try {
                await this._favoritesService.removeFavorite(favorite);
            } catch (ex) {
                // set the error and favorite value back
                reportError(this.context.pageContext, "unfavorite-query", ex?.message);
                this.props.item.isFavoriteObservable.value = true;
                if (this.props.onAddFavorite) {
                    this.props.onAddFavorite(event);
                }
            }
        }
    };
}
